import React from "react";
import { useSearchParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { EditableTagList } from "../Shared/NameTagEdit/EditableTagList";
import { useMetaEditor } from "../Shared/NameTagEdit/useMetaEditor";
import { DetailRow } from "../Shared/DetailRow";
import { DetailLink } from "../Shared/DetailLink";
import CarrierAccessSummary from "./CarrierAccessSummary";
import ExtenderPopConnections from "./ExtenderPopConnections";
import CpesCard from "./CpesCard";
import SiteLocationRows from "./SiteLocationRows";
import { cpeDetailPath, extenderPopStatusPresentation } from "./ExtenderPopData";
import {
  LoadingDetail,
  formatRenewDate,
  formatTerm,
  legacyTagsNoop,
  useExtenderPopDetail,
} from "./ExtenderPopDetailShared";
import { cpeSiteTypeLabel, shapeSiteLocation } from "./cpeSite";

/**
 * Detail view for one CPE ("Extended PoP" in the code): the customer site
 * (85-), homologated with Port Details — details left, Status right — then
 * its routers and every connection the site delivers, one by one with the
 * router behind it. Each router has its own detail with its links,
 * bandwidth and connections (ExtenderPopCpeDetails).
 * Customer-facing on purpose — no internal hostnames or hardware identity.
 */
const ExtenderPopDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const extenderPopId = Number(searchParams.get("extenderPopId"));
  const { tenantId } = useAppSelector((state) => state.selection);
  const api = useAxiosAuth0();

  const { data: detail, error, reload } = useExtenderPopDetail(
    extenderPopId,
    tenantId
  );
  // The name is edited in place, like Port Details. Tags are not: they go
  // through EditableTagList and the Tags API.
  const meta = useMetaEditor({
    api,
    kind: "extender-pop",
    resourceId: detail ? String(detail.id) : null,
    tenantId,
    initialName: detail?.name ?? "",
    initialTag: "",
  });

  const saveName = (newValue: string) => {
    const nextName = newValue.trim();
    if (!nextName) {
      meta.cancelName();
      return;
    }
    meta.setNameValue(nextName);
    meta.saveName(nextName).catch(() => meta.cancelName());
  };

  if (error) {
    return <ErrorFetchingItems text={error} />;
  }

  if (!detail) {
    return <LoadingDetail />;
  }

  const status = extenderPopStatusPresentation(detail.status);
  const activeCpe = detail.cpes.find((cpe) => cpe.role === "active");
  // The term is the active router's: each router is an Odoo line with its
  // own subscription, and the list shows the same one.
  const subscription = (activeCpe ?? detail.cpes[0])?.subscription ?? null;

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {/* Card 1 — homolog of "Port Details" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="CPE Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="ID" value={detail.serviceId} />
                <DetailRow
                  label="Name"
                  value={
                    <GenericTag
                      label={meta.nameValue || "—"}
                      variant="neutral"
                      size="small"
                      minWidth="100px"
                      className="text-center"
                      editable={true}
                      value={meta.nameValue}
                      onSave={saveName}
                      onCancel={meta.cancelName}
                      isSaving={meta.savingField === "name"}
                      disabled={meta.savingField !== null}
                    />
                  }
                />
                <div className="flex justify-between w-full py-1 border-b">
                  <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <EditableTagList
                      tags={[]}
                      onUpdate={legacyTagsNoop}
                      resourceType="ke-extender-pop"
                      resourceId={detail.id}
                      tenantId={detail.tenantId}
                    />
                  </div>
                </div>
                <DetailRow
                  label="Type"
                  value={cpeSiteTypeLabel(detail.cpes.length)}
                />
                <SiteLocationRows
                  extenderPopId={detail.id}
                  tenantId={tenantId}
                  location={shapeSiteLocation(detail)}
                  onSaved={reload}
                />
                {/* One entry per sold access, by id, as the list shows it. */}
                <DetailRow
                  label="Backhaul"
                  value={
                    detail.carrierAccess.length > 0 ? (
                      <CarrierAccessSummary
                        carriers={detail.carrierAccess}
                        align="end"
                      />
                    ) : null
                  }
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 2 — homolog of "Status" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Status"
            bodyContent={
              <div className="space-y-2">
                <DetailRow
                  label="Service Status"
                  value={
                    <GenericTag
                      variant={status.variant}
                      label={status.label}
                      size="small"
                    />
                  }
                />
                <DetailRow
                  label="Active Device"
                  value={
                    activeCpe ? (
                      <DetailLink
                        to={cpeDetailPath(detail.id, activeCpe.id)}
                        // Devices go by name; the CPE has the one id.
                        label={activeCpe.name || activeCpe.serviceId}
                      />
                    ) : null
                  }
                />
                <DetailRow label="Term" value={formatTerm(subscription)} />
                <DetailRow
                  label="Auto Renew"
                  value={subscription?.autoRenew || null}
                />
                <DetailRow
                  label="Renew Date"
                  value={formatRenewDate(subscription)}
                />
                <DetailRow
                  label="Subscription Status"
                  value={subscription?.status || null}
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 3 — the site's routers, the pair side by side */}
        <div className="flex w-full self-start md:col-span-2">
          <CpesCard extenderPopId={detail.id} tenantId={tenantId} cpes={detail.cpes} />
        </div>

        {/* Card 4 — what the whole site delivers, one by one */}
        <div className="w-full md:col-span-2 pb-4">
          <ExtenderPopConnections
            extenderPopId={detail.id}
            tenantId={tenantId}
            cpes={detail.cpes}
          />
        </div>
      </div>
    </div>
  );
};

export default ExtenderPopDetails;
